import { useCallback, useEffect, useRef, useState } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const API =
  (process.env.REACT_APP_LLM_BASE_URL || "http://127.0.0.1:8000").replace(/\/+$/, "") + "/IpVijil";

const AUTH_KEY = "qaPageAuthed";

type StepResult = {
  index: number;
  progress: string;
  title: string;
  status: "PASS" | "WARN" | "FAIL";
  issues: string[];
  target: string;
  target_text: string;
  rect: number[] | null;
  path: string;
  screenshot: string;
};

type TourResult = {
  name: string;
  status: "PASS" | "WARN" | "FAIL";
  started_from: string;
  expected_steps: number | null;
  steps: StepResult[];
  issues: string[];
};

type RunInfo = {
  id: string;
  status: string;
  base_url?: string;
  only?: string[];
  started_at?: number;
  generated_at?: number;
  counts?: { PASS: number; WARN: number; FAIL: number };
};

type Config = {
  auth_exists: boolean;
  running: boolean;
  login_pending: boolean;
  tours: string[];
};

type Shot = { file: string; label: string };

const StatusIcon = ({ status }: { status: string }) =>
  status === "PASS" ? (
    <CheckCircleIcon sx={{ fontSize: 18, color: "#067647" }} />
  ) : status === "WARN" ? (
    <WarningAmberIcon sx={{ fontSize: 18, color: "#B54708" }} />
  ) : (
    <CancelIcon sx={{ fontSize: 18, color: "#B42318" }} />
  );

const statusChip = (status: string) =>
  status === "PASS"
    ? "bg-[#ECFDF3] text-[#067647] border-[#ABEFC6]"
    : status === "WARN"
    ? "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
    : "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]";

const runDate = (r: RunInfo) => {
  const t = r.generated_at || r.started_at;
  if (t) return new Date(t * 1000).toLocaleString();
  const m = r.id.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}` : r.id;
};

const runLabel = (r: RunInfo) => {
  const c = r.counts;
  const tail =
    r.status === "done" && c
      ? ` — ${c.PASS} pass · ${c.WARN} warn · ${c.FAIL} fail`
      : ` — ${r.status}`;
  return `${runDate(r)}${tail}`;
};

const QaPage = () => {
  const [authed, setAuthed] = useState(() => localStorage.getItem(AUTH_KEY) === "1");
  const [authUser, setAuthUser] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authError, setAuthError] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  const [config, setConfig] = useState<Config | null>(null);
  const [sectionOpen, setSectionOpen] = useState(true);
  const [baseUrl, setBaseUrl] = useState("https://demo.ipvijil.ai");
  const [selected, setSelected] = useState<string[]>([]);
  const [headed, setHeaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState("");
  const [error, setError] = useState("");
  const [loginPending, setLoginPending] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [results, setResults] = useState<TourResult[] | null>(null);
  const [reportRunId, setReportRunId] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<number | null>(null);
  const [openTour, setOpenTour] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ shots: Shot[]; index: number } | null>(null);
  const [showExpectations, setShowExpectations] = useState(false);
  const [expectationsText, setExpectationsText] = useState("");
  const [expSaved, setExpSaved] = useState("");
  const logRef = useRef<HTMLPreElement>(null);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/qa/walkthrough/config`);
      const c = await res.json();
      setConfig(c);
      setRunning(c.running);
      setLoginPending(c.login_pending);
    } catch {
      setConfig(null);
    }
  }, []);

  const loadReport = useCallback(async (runId?: string) => {
    try {
      const res = await fetch(`${API}/qa/walkthrough/report${runId ? `?run=${runId}` : ""}`);
      if (!res.ok) return;
      const r = await res.json();
      setResults(r.results);
      setReportRunId(r.id);
      setGeneratedAt(r.generated_at);
    } catch {
      /* no report yet */
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const res = await fetch(`${API}/qa/walkthrough/runs`);
      if (res.ok) setRuns(await res.json());
    } catch {
      /* server down */
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadConfig();
    loadRuns();
    loadReport();
  }, [authed, loadConfig, loadRuns, loadReport]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${API}/qa/walkthrough/status`);
        const s = await res.json();
        setLog(s.log || "");
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
        if (!s.running) {
          setRunning(false);
          loadRuns();
          loadReport();
          loadConfig();
        }
      } catch {
        /* server restarting */
      }
    }, 2000);
    return () => clearInterval(id);
  }, [running, loadRuns, loadReport, loadConfig]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft")
        setLightbox((lb) => (lb ? { ...lb, index: Math.max(0, lb.index - 1) } : lb));
      if (e.key === "ArrowRight")
        setLightbox((lb) =>
          lb ? { ...lb, index: Math.min(lb.shots.length - 1, lb.index + 1) } : lb
        );
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const post = async (path: string, body?: object) => {
    setError("");
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || `${res.status} ${res.statusText}`);
    }
    return res.json();
  };

  const pageLogin = async () => {
    try {
      setAuthBusy(true);
      setAuthError("");
      await post("/qa/auth/login", { username: authUser, password: authPass });
      localStorage.setItem(AUTH_KEY, "1");
      setAuthed(true);
    } catch (e: any) {
      setAuthError(e.message);
    } finally {
      setAuthBusy(false);
    }
  };

  const submitLogin = async () => {
    try {
      setLoggingIn(true);
      await post("/qa/walkthrough/login", { base_url: baseUrl, email, password });
      setShowLoginForm(false);
      setPassword("");
      loadConfig();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const startWindowLogin = async () => {
    try {
      await post("/qa/walkthrough/login", { base_url: baseUrl });
      setShowLoginForm(false);
      setLoginPending(true);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const confirmLogin = async () => {
    try {
      const r = await post("/qa/walkthrough/login/confirm");
      setLoginPending(false);
      if (!r.logged_in)
        setError(
          "That browser session wasn't logged in — click Log in again, sign into the app inside the \"Google Chrome for Testing\" window, THEN click done."
        );
      loadConfig();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const startRun = async () => {
    try {
      setResults(null);
      setReportRunId(null);
      setLog("");
      await post("/qa/walkthrough/run", { base_url: baseUrl, only: selected, headed });
      setRunning(true);
      loadRuns();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const stopRun = async () => {
    try {
      await post("/qa/walkthrough/stop");
      setRunning(false);
      loadRuns();
      loadConfig();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteRun = async (runId: string) => {
    try {
      const res = await fetch(`${API}/qa/walkthrough/runs/${runId}`, { method: "DELETE" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "delete failed");
      }
      setResults(null);
      setReportRunId(null);
      await loadRuns();
      loadReport();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleExpectations = async () => {
    if (!showExpectations && !expectationsText) {
      try {
        const res = await fetch(`${API}/qa/walkthrough/expectations`);
        setExpectationsText(await res.text());
      } catch {
        setError("Could not load expectations.json");
        return;
      }
    }
    setShowExpectations(!showExpectations);
  };

  const saveExpectations = async () => {
    try {
      setExpSaved("");
      const res = await fetch(`${API}/qa/walkthrough/expectations`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: expectationsText }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "save failed");
      }
      setExpSaved("Saved ✓");
      loadConfig();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleTour = (name: string) =>
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );

  const openLightbox = (tour: TourResult, step: StepResult) => {
    const shots = tour.steps
      .filter((s) => s.screenshot)
      .map((s) => ({
        file: s.screenshot,
        label: `${tour.name} — ${s.progress || `Step ${s.index}`} ${s.title}`,
      }));
    const index = shots.findIndex((s) => s.file === step.screenshot);
    if (index >= 0) setLightbox({ shots, index });
  };

  const shotUrl = (file: string) =>
    `${API}/qa/walkthrough/screens/${reportRunId}/${file}`;

  const counts = results
    ? {
        pass: results.filter((r) => r.status === "PASS").length,
        warn: results.filter((r) => r.status === "WARN").length,
        fail: results.filter((r) => r.status === "FAIL").length,
      }
    : null;

  // ---- page auth gate ----
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-6">
        <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-8 w-[380px]">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#F5F8FD] flex items-center justify-center">
              <BugReportOutlinedIcon sx={{ fontSize: 22, color: "#1751D0" }} />
            </div>
            <div>
              <h1 className="text-lg font-interSemiBold text-[#0A090B]">QA</h1>
              <p className="text-xs font-interRegular text-[#667085]">Sign in to continue</p>
            </div>
          </div>
          <div className="space-y-3">
            <input
              className="w-full border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular focus:outline-none focus:border-[#1751D0]"
              placeholder="Username"
              value={authUser}
              onChange={(e) => setAuthUser(e.target.value)}
              autoComplete="username"
            />
            <input
              className="w-full border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular focus:outline-none focus:border-[#1751D0]"
              placeholder="Password"
              type="password"
              value={authPass}
              onChange={(e) => setAuthPass(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && authUser && authPass && pageLogin()}
              autoComplete="current-password"
            />
            {authError && (
              <p className="text-xs font-interRegular text-[#B42318]">{authError}</p>
            )}
            <button
              type="button"
              onClick={pageLogin}
              disabled={!authUser || !authPass || authBusy}
              className="w-full bg-[#1751D0] text-white rounded-lg px-4 py-2 text-sm font-interSemiBold hover:bg-[#1243AC] disabled:opacity-50"
            >
              {authBusy ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] px-6 py-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#F5F8FD] flex items-center justify-center">
            <BugReportOutlinedIcon sx={{ fontSize: 22, color: "#1751D0" }} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-interSemiBold text-[#0A090B]">QA</h1>
            <p className="text-sm font-interRegular text-[#667085]">
              Automated checks against the running app
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem(AUTH_KEY);
              setAuthed(false);
            }}
            className="text-xs font-interRegular text-[#667085] underline"
          >
            log out
          </button>
        </div>

        {/* Guided Walkthrough Test section */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm">
          <button
            type="button"
            className="w-full flex items-center justify-between px-5 py-4"
            onClick={() => setSectionOpen(!sectionOpen)}
          >
            <div className="flex items-center gap-3">
              <span className="text-base font-interSemiBold text-[#0A090B]">
                Guided Walkthrough Test
              </span>
              {config && (
                <span
                  className={`text-xs font-interMedium px-2 py-0.5 rounded-full border ${
                    config.auth_exists
                      ? "bg-[#ECFDF3] text-[#067647] border-[#ABEFC6]"
                      : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
                  }`}
                >
                  {config.auth_exists ? "session saved" : "login required"}
                </span>
              )}
              {counts && (
                <span className="text-xs font-interRegular text-[#667085]">
                  {counts.pass} pass · {counts.warn} warn · {counts.fail} fail
                </span>
              )}
            </div>
            <ExpandMoreIcon
              sx={{
                fontSize: 22,
                color: "#667085",
                transform: sectionOpen ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </button>

          {sectionOpen && (
            <div className="border-t border-[#E5E7EB] px-5 py-4 space-y-4">
              {config === null && (
                <div className="text-sm font-interRegular text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-lg px-3 py-2">
                  Python service unreachable at {API}. Start it with{" "}
                  <code className="font-mono">uvicorn main:app --reload</code> in
                  ipvijil-ai-services.
                </div>
              )}

              {/* Controls */}
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-interMedium text-[#667085]">Base URL</label>
                  <input
                    className="border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular w-[260px] focus:outline-none focus:border-[#1751D0]"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    disabled={running}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm font-interRegular text-[#0A090B] pb-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={headed}
                    onChange={(e) => setHeaded(e.target.checked)}
                    disabled={running}
                  />
                  Show browser
                </label>
                <div className="flex-1" />
                {!config?.auth_exists && !loginPending && (
                  <button
                    type="button"
                    onClick={() => setShowLoginForm(!showLoginForm)}
                    className="flex items-center gap-1.5 border border-[#1751D0] text-[#1751D0] bg-[#F5F8FD] rounded-lg px-4 py-2 text-sm font-interSemiBold hover:bg-[#EAF0FB]"
                  >
                    <LoginOutlinedIcon sx={{ fontSize: 17 }} /> Log in
                  </button>
                )}
                {config?.auth_exists && !loginPending && !running && (
                  <button
                    type="button"
                    onClick={() => setShowLoginForm(!showLoginForm)}
                    className="text-xs font-interRegular text-[#667085] underline pb-3"
                  >
                    re-login
                  </button>
                )}
                {running ? (
                  <button
                    type="button"
                    onClick={stopRun}
                    className="flex items-center gap-1.5 bg-[#B42318] text-white rounded-lg px-4 py-2 text-sm font-interSemiBold hover:bg-[#912018]"
                  >
                    <StopIcon sx={{ fontSize: 17 }} /> Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRun}
                    disabled={!config?.auth_exists}
                    className="flex items-center gap-1.5 bg-[#1751D0] text-white rounded-lg px-4 py-2 text-sm font-interSemiBold hover:bg-[#1243AC] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlayArrowIcon sx={{ fontSize: 17 }} />
                    Run {selected.length ? `${selected.length} selected` : "all"}
                  </button>
                )}
              </div>

              {showLoginForm && (
                <div className="bg-[#F5F8FD] border border-[#B2CCFF] rounded-lg px-4 py-3 space-y-2">
                  <p className="text-sm font-interMedium text-[#0A090B]">
                    Sign in to {baseUrl.replace(/^https?:\/\//, "")} — credentials are only used
                    locally to log in; only the browser session is stored.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular w-[240px] focus:outline-none focus:border-[#1751D0]"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                    />
                    <input
                      className="border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular w-[200px] focus:outline-none focus:border-[#1751D0]"
                      placeholder="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && email && password && submitLogin()}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={submitLogin}
                      disabled={!email || !password || loggingIn}
                      className="bg-[#1751D0] text-white rounded-lg px-4 py-2 text-sm font-interSemiBold hover:bg-[#1243AC] disabled:opacity-50"
                    >
                      {loggingIn ? "Signing in…" : "Sign in & save session"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={startWindowLogin}
                    className="text-xs font-interRegular text-[#667085] underline"
                  >
                    or open a browser window to log in manually
                  </button>
                </div>
              )}

              {loginPending && (
                <div className="flex items-center justify-between bg-[#F5F8FD] border border-[#B2CCFF] rounded-lg px-3 py-2">
                  <p className="text-sm font-interRegular text-[#0A090B]">
                    A &quot;Google Chrome for Testing&quot; window opened on this machine (it may
                    be behind other windows) — log in to IpVijil there, then click done.
                  </p>
                  <button
                    type="button"
                    onClick={confirmLogin}
                    className="bg-[#1751D0] text-white rounded-lg px-3 py-1.5 text-sm font-interSemiBold hover:bg-[#1243AC] shrink-0"
                  >
                    I&apos;ve logged in
                  </button>
                </div>
              )}

              {/* Tour filter chips */}
              {!!config?.tours.length && (
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setSelected([])}
                    disabled={running}
                    className={`text-xs font-interMedium px-2.5 py-1 rounded-full border ${
                      selected.length === 0
                        ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD]"
                        : "border-[#CED2DA] text-[#667085] bg-white hover:border-[#1751D0]"
                    }`}
                  >
                    All walkthroughs
                  </button>
                  {config.tours.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTour(t)}
                      disabled={running}
                      className={`text-xs font-interMedium px-2.5 py-1 rounded-full border ${
                        selected.includes(t)
                          ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD]"
                          : "border-[#CED2DA] text-[#667085] bg-white hover:border-[#1751D0]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}

              {error && (
                <div className="text-sm font-interRegular text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              {/* Live log */}
              {(running || (log && !results)) && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {running && (
                      <span className="w-2 h-2 rounded-full bg-[#1751D0] animate-pulse" />
                    )}
                    <span className="text-xs font-interMedium text-[#667085]">
                      {running ? "Running…" : "Last output"}
                    </span>
                  </div>
                  <pre
                    ref={logRef}
                    className="bg-[#0A090B] text-[#D0D5DD] text-xs font-mono rounded-lg p-3 max-h-[220px] overflow-auto whitespace-pre-wrap"
                  >
                    {log || "starting…"}
                  </pre>
                </div>
              )}

              {/* Run history (newest first) */}
              {runs.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs font-interMedium text-[#667085] shrink-0">Runs</label>
                  <select
                    className="border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular bg-white focus:outline-none focus:border-[#1751D0] max-w-[420px]"
                    value={reportRunId || ""}
                    onChange={(e) => e.target.value && loadReport(e.target.value)}
                  >
                    {!reportRunId && <option value="">select a run…</option>}
                    {runs.map((r) => (
                      <option key={r.id} value={r.id} disabled={r.status !== "done"}>
                        {runLabel(r)}
                      </option>
                    ))}
                  </select>
                  {reportRunId && (
                    <button
                      type="button"
                      title="Delete this run"
                      onClick={() => deleteRun(reportRunId)}
                      className="border border-[#CED2DA] rounded-lg p-2 text-[#667085] hover:text-[#B42318] hover:border-[#B42318]"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </button>
                  )}
                </div>
              )}

              {/* Report */}
              {results && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-interSemiBold text-[#0A090B]">
                      Report
                      {generatedAt && (
                        <span className="font-interRegular text-[#667085] text-xs ml-2">
                          {new Date(generatedAt * 1000).toLocaleString()}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                    {results.map((r) => (
                      <div key={r.name} className="border-b border-[#E5E7EB] last:border-b-0">
                        <button
                          type="button"
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFBFC] text-left"
                          onClick={() => setOpenTour(openTour === r.name ? null : r.name)}
                        >
                          <StatusIcon status={r.status} />
                          <span className="text-sm font-interMedium text-[#0A090B] flex-1 min-w-0 truncate">
                            {r.name}
                          </span>
                          <span className="text-xs font-interRegular text-[#667085] shrink-0">
                            {r.steps.length}
                            {r.expected_steps ? `/${r.expected_steps}` : ""} steps
                          </span>
                          <span
                            className={`text-xs font-interMedium px-2 py-0.5 rounded-full border shrink-0 ${statusChip(
                              r.status
                            )}`}
                          >
                            {r.status}
                          </span>
                          <ExpandMoreIcon
                            sx={{
                              fontSize: 18,
                              color: "#667085",
                              transform: openTour === r.name ? "rotate(180deg)" : "none",
                            }}
                          />
                        </button>
                        {openTour === r.name && (
                          <div className="px-4 pb-3 bg-[#FAFBFC]">
                            {(r.issues.length > 0 || r.started_from !== "help-center") && (
                              <div className="text-xs font-interRegular text-[#B54708] py-2">
                                Started from: {r.started_from || "never"}
                                {r.issues.map((x) => (
                                  <div key={x}>• {x}</div>
                                ))}
                              </div>
                            )}
                            {r.steps.map((s) => (
                              <div
                                key={s.index}
                                className="flex items-start gap-3 py-2 border-t border-[#E5E7EB]"
                              >
                                <StatusIcon status={s.status} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-interMedium text-[#0A090B]">
                                    {s.progress || `Step ${s.index}`} — {s.title}
                                  </p>
                                  {s.issues.map((x) => (
                                    <p
                                      key={x}
                                      className="text-xs font-interRegular text-[#B42318]"
                                    >
                                      {x}
                                    </p>
                                  ))}
                                  <p className="text-xs font-mono text-[#667085] truncate">
                                    {s.target} · {s.path}
                                  </p>
                                </div>
                                {s.screenshot && (
                                  <img
                                    src={shotUrl(s.screenshot)}
                                    alt={s.title}
                                    className="w-[120px] h-[72px] object-cover object-top rounded border border-[#CED2DA] cursor-zoom-in shrink-0"
                                    onClick={() => openLightbox(r, s)}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expectations editor */}
              <div>
                <button
                  type="button"
                  onClick={toggleExpectations}
                  className="flex items-center gap-1.5 text-sm font-interMedium text-[#667085] hover:text-[#1751D0]"
                >
                  <TuneOutlinedIcon sx={{ fontSize: 16 }} />
                  {showExpectations ? "Hide" : "Edit"} expectations.json
                </button>
                {showExpectations && (
                  <div className="mt-2 space-y-2">
                    <textarea
                      className="w-full h-[300px] border border-[#CED2DA] rounded-lg p-3 text-xs font-mono focus:outline-none focus:border-[#1751D0]"
                      value={expectationsText}
                      onChange={(e) => {
                        setExpectationsText(e.target.value);
                        setExpSaved("");
                      }}
                      spellCheck={false}
                    />
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={saveExpectations}
                        className="bg-[#1751D0] text-white rounded-lg px-4 py-1.5 text-sm font-interSemiBold hover:bg-[#1243AC]"
                      >
                        Save
                      </button>
                      {expSaved && (
                        <span className="text-xs font-interMedium text-[#067647]">{expSaved}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Screenshot lightbox with prev/next */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white"
            onClick={() => setLightbox(null)}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </button>
          {lightbox.index > 0 && (
            <button
              type="button"
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-3"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox({ ...lightbox, index: lightbox.index - 1 });
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 24 }} />
            </button>
          )}
          {lightbox.index < lightbox.shots.length - 1 && (
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-3"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox({ ...lightbox, index: lightbox.index + 1 });
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 24 }} />
            </button>
          )}
          <div
            className="flex flex-col items-center gap-3 max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={shotUrl(lightbox.shots[lightbox.index].file)}
              alt={lightbox.shots[lightbox.index].label}
              className="max-w-full max-h-[82vh] rounded-lg shadow-2xl"
            />
            <p className="text-white text-sm font-interRegular text-center">
              {lightbox.shots[lightbox.index].label}
              <span className="text-white/60 ml-2">
                {lightbox.index + 1} / {lightbox.shots.length}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QaPage;
